"use client";

import { useWebMcpTool } from "@/lib/webmcp/react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText } from "@/lib/funnel-planner/i18n";
import type { PlannerPackageAdvice } from "@/lib/funnel-planner/package-adviser";
import {
  FUNNEL_PACKAGE_TIERS,
  type FunnelPackageTier,
  type PackageAdviserDecision,
} from "@/lib/funnel-planner/schema";
import { z } from "zod";

const packageAdviserInput = z.discriminatedUnion("decision", [
  z.object({ decision: z.literal("none") }).strict(),
  z.object({ decision: z.literal("accepted") }).strict(),
  z.object({ decision: z.literal("overridden"), tier: z.enum(FUNNEL_PACKAGE_TIERS) }).strict(),
  z.object({ decision: z.literal("skipped") }).strict(),
]);

/**
 * NL-041 adviser surface.
 *
 * Advisory only. It presents a suggested plan and, where a caller-owned pricing
 * snapshot applies, an indicative range. It never presents a quote, a total, an
 * invoice, or a checkout action, and an incomplete pricing context shows the
 * manual-scoping message instead of a number.
 */
export function PackageAdviserPanel({
  advice,
  approximate = false,
  content,
  onDecision,
  readOnly = false,
}: {
  advice: PlannerPackageAdvice;
  approximate?: boolean;
  content: FunnelPlannerContent;
  onDecision: (decision: PackageAdviserDecision, tier?: FunnelPackageTier | null) => void;
  /** When viewing another visitor's shared plan, the decision is not editable. */
  readOnly?: boolean;
}) {
  const decided = advice.decision !== "none";
  const showBand = !advice.manualQuoteRequired && advice.pricingBand !== null;

  useWebMcpTool({
    action: "apply_advisory",
    instance: "package_adviser",
    schema: packageAdviserInput,
    enabled: !readOnly,
    execute: (input) => {
      if (readOnly) return { status: "unavailable" as const };

      if (input.decision === "accepted") {
        onDecision("accepted", advice.recommendedTier);
        return {
          status: "success" as const,
          uiState: { decision: "accepted", tier: advice.recommendedTier },
        };
      }

      if (input.decision === "overridden") {
        onDecision("overridden", input.tier);
        return {
          status: "success" as const,
          uiState: { decision: "overridden", tier: input.tier },
        };
      }

      onDecision(input.decision, null);
      return { status: "success" as const, uiState: { decision: input.decision } };
    },
  });

  return (
    <section
      aria-label={funnelText(content, "packageAdviserSectionAria", "Package suggestion")}
      className="rounded-xl border bg-muted/30 p-4"
      data-funnel-planner-package-adviser="true"
      data-package-adviser-decision={advice.decision}
      data-package-adviser-manual-quote={advice.manualQuoteRequired ? "true" : "false"}
      data-package-adviser-recommended-tier={advice.recommendedTier}
    >
      <h3 className="text-base font-bold">
        {funnelText(content, "packageAdviserTitle", "Suggested plan for this funnel")}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {funnelText(
          content,
          "packageAdviserIntro",
          "Based on the scope you have mapped so far. You can accept it, pick a different plan, or skip this entirely.",
        )}
      </p>

      <p className="mt-3 text-sm font-semibold">
        {funnelText(content, "packageAdviserRecommended", "Suggested plan: {tier}", {
          tier: advice.recommendedTier,
        })}
      </p>

      {advice.reasons.length > 0 ? (
        <div className="mt-2">
          <p className="text-xs font-semibold text-muted-foreground">
            {funnelText(content, "packageAdviserReasonsLabel", "Why this plan")}
          </p>
          <ul className="mt-1 list-disc text-xs text-foreground" style={{ paddingInlineStart: 18 }}>
            {advice.reasons.map((reason) => (
              <li key={reason}>
                {funnelText(
                  content,
                  `packageAdviserReason.${reason}`,
                  funnelText(
                    content,
                    "calculationExplanationUnavailableDetail",
                    "Additional detail is unavailable.",
                  ),
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showBand ? (
        <div className="mt-3 rounded-lg border bg-muted/20 p-3">
          <p className="text-xs font-semibold text-muted-foreground">
            {funnelText(content, "packageAdviserBandLabel", "Indicative range")}
          </p>
          <p className="mt-1 text-sm font-bold text-foreground" data-package-adviser-band="true">
            {advice.pricingBand}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {funnelText(
              content,
              "packageAdviserAdvisoryNote",
              "Indicative planning range only. It is not a quote, an offer, or a price we have agreed.",
            )}
          </p>
          {approximate ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {funnelText(
                content,
                "packageAdviserApproximate",
                "Converted from US dollars at an indicative rate, so treat it as a rough guide.",
              )}
            </p>
          ) : null}
        </div>
      ) : (
        <p
          className="mt-3 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground"
          data-package-adviser-manual-notice="true"
        >
          {funnelText(
            content,
            "packageAdviserManualQuote",
            "We cannot show an indicative range for this setup, so this one needs a person to scope it.",
          )}
        </p>
      )}

      {decided ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-sm" role="status" data-package-adviser-status="true">
            {advice.decision === "accepted"
              ? funnelText(content, "packageAdviserAcceptedStatus", "Planning with {tier}.", {
                  tier: advice.selectedTier ?? advice.recommendedTier,
                })
              : advice.decision === "overridden"
                ? funnelText(
                    content,
                    "packageAdviserOverriddenStatus",
                    "You chose {tier} instead of the suggested {recommended}.",
                    {
                      tier: advice.selectedTier ?? advice.recommendedTier,
                      recommended: advice.recommendedTier,
                    },
                  )
                : funnelText(
                    content,
                    "packageAdviserSkippedStatus",
                    "Skipped. No plan is attached to this funnel.",
                  )}
          </p>
          <button
            className="rounded-md border px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-50"
            disabled={readOnly}
            onClick={() => onDecision("none", null)}
            type="button"
          >
            {funnelText(content, "packageAdviserChange", "Change")}
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            disabled={readOnly}
            onClick={() => onDecision("accepted", advice.recommendedTier)}
            type="button"
          >
            {funnelText(content, "packageAdviserAccept", "Use this suggestion")}
          </button>

          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{funnelText(content, "packageAdviserOverrideLabel", "Use a different plan")}</span>
            <select
              aria-label={funnelText(content, "packageAdviserOverrideLabel", "Use a different plan")}
              className="rounded-md border bg-background px-2 py-1 text-xs disabled:opacity-50"
              disabled={readOnly}
              onChange={(event) => onDecision("overridden", event.target.value as FunnelPackageTier)}
              value=""
            >
              <option disabled value="">
                {funnelText(content, "packageAdviserOverridePlaceholder", "Select a package tier")}
              </option>
              {FUNNEL_PACKAGE_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {tier}
                </option>
              ))}
            </select>
          </label>

          <button
            className="rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50"
            disabled={readOnly}
            onClick={() => onDecision("skipped", null)}
            type="button"
          >
            {funnelText(content, "packageAdviserSkip", "Skip this step")}
          </button>
        </div>
      )}
    </section>
  );
}
